import { MenuProps } from '@mui/material';
import { orderBy } from 'lodash';
import { useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';

import {
  SelectInputOption,
  TypeaheadSelectInputOption,
} from '@/components/form/baseInputs/inputTypes';
import { getOptionsWithGroupLabels } from '@/components/form/baseInputs/SelectInput/SelectInput.utils';
import { FormAwareCurrencyInput } from '@/components/form/formAwareInputs/FormAwareCurrencyInput';
import { FormAwareSelectInput } from '@/components/form/formAwareInputs/FormAwareSelectInput';
import { FormAwareTextInput } from '@/components/form/formAwareInputs/FormAwareTextInput';
import { PlusIcon } from '@/components/icons/PlusIcon';
import { FormLayoutItem, FormLayoutRow } from '@/components/layout/FormLayout';
import { PopperContent } from '@/components/poppers/PopperContent';
import { useFormContext } from '@/components/react-hook-form';
import { STATE_CODE_TO_NAME } from '@/constants/states';
import { CreateAssetCategoryModal } from '@/modules/assetCategories/CreateAssetCategoryModal/CreateAssetCategoryModal';
import {
  BehaviorAuthorizationType,
  useHasBehaviorAuthorization,
} from '@/modules/authentication/hooks/useHasBehaviorAuthorization';
import { useUnguardedHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { StateCode } from '@/types/schema';

import {
  Fields,
  STRUCTURED_ASSETS_SUBFORM_NAMESPACE,
  SubformField,
  TAX_SITUS_GRANTOR_PREFIX,
  TAX_SITUS_STATE_PREFIX,
} from './StructuredAssetsSubform.types';

interface AssetCategoryFieldProps {
  index: number;
  hideLabel?: boolean;
  options: TypeaheadSelectInputOption<string>[];
  MenuProps?: Partial<MenuProps>;
}

export const AssetCategoryField = ({
  index,
  hideLabel,
  options,
  MenuProps: menuProps,
}: AssetCategoryFieldProps) => {
  const userCanCreateAssetCategories = useHasBehaviorAuthorization(
    BehaviorAuthorizationType.CAN_MODIFY_ASSET_CATEGORIES
  );
  const { control } = useFormContext<Fields>();
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <>
      <FormAwareSelectInput<Fields>
        fieldName={
          `${STRUCTURED_ASSETS_SUBFORM_NAMESPACE}.assets.${index}.categoryId` as const satisfies SubformField
        }
        label="Asset category"
        required
        showEmptyValue={false}
        hideLabel={hideLabel}
        control={control}
        options={options}
        addNewOption={
          userCanCreateAssetCategories
            ? {
                onClick: () => setIsModalOpen(true),
                icon: <PlusIcon size={16} />,
              }
            : undefined
        }
        MenuProps={menuProps}
      />
      <CreateAssetCategoryModal
        isOpen={isModalOpen}
        existingOptionsCount={options.length}
        onClose={() => setIsModalOpen(false)}
      />
    </>
  );
};

interface AssetTitleFieldProps {
  index: number;
  hideLabel?: boolean;
  label?: string;
  required?: boolean;
}

export const AssetTitleField = ({
  index,
  hideLabel,
  label = 'Title',
  required = true,
}: AssetTitleFieldProps) => {
  const { control } = useFormContext<Fields>();

  return (
    <FormAwareTextInput<Fields>
      fieldName={
        `${STRUCTURED_ASSETS_SUBFORM_NAMESPACE}.assets.${index}.title` as const satisfies SubformField
      }
      label={label}
      required={required}
      hideLabel={hideLabel}
      control={control}
    />
  );
};

interface AssetValueFieldProps {
  index: number;
  hideLabel?: boolean;
  required?: boolean;
}

export const AssetValueField = ({
  index,
  hideLabel,
  required = true,
}: AssetValueFieldProps) => {
  const { control } = useFormContext<Fields>();

  return (
    <FormAwareCurrencyInput<Fields>
      isDecimalJSInput
      fieldName={
        `${STRUCTURED_ASSETS_SUBFORM_NAMESPACE}.assets.${index}.value` as const satisfies SubformField
      }
      label="Value"
      required={required}
      hideLabel={hideLabel}
      control={control}
    />
  );
};

interface StructuredAssetRowFieldsProps {
  index: number;
  options: TypeaheadSelectInputOption<string>[];
  // When false, the title/value fields are not required. Used to let a single
  // empty row submit as "no assets".
  required?: boolean;
  titleLabel?: string;
  // Applied to every Select menu in the row (category + state). Used to lift the
  // menus above a containing SideDrawer, whose z-index sits above the modal layer.
  selectMenuProps?: Partial<MenuProps>;
}

// Shared row layout for the structured-assets repeater, used by both the
// StructuredAssetsSubform UI and the holdings-group edit form.
export const StructuredAssetRowFields = ({
  index,
  options,
  required = true,
  titleLabel = 'Name',
  selectMenuProps,
}: StructuredAssetRowFieldsProps) => {
  const isFirstInput = index === 0;
  const columnWidth = 3;

  return (
    <FormLayoutRow>
      <FormLayoutItem width={columnWidth}>
        <AssetTitleField
          index={index}
          hideLabel={!isFirstInput}
          label={titleLabel}
          required={required}
        />
      </FormLayoutItem>
      <FormLayoutItem width={columnWidth}>
        <AssetCategoryField
          index={index}
          hideLabel={!isFirstInput}
          options={options}
          MenuProps={selectMenuProps}
        />
      </FormLayoutItem>
      <FormLayoutItem width={columnWidth}>
        <AssetStateField
          index={index}
          hideLabel={!isFirstInput}
          MenuProps={selectMenuProps}
        />
      </FormLayoutItem>
      <FormLayoutItem width={columnWidth}>
        <AssetValueField
          index={index}
          hideLabel={!isFirstInput}
          required={required}
        />
      </FormLayoutItem>
    </FormLayoutRow>
  );
};

interface AssetStateFieldProps {
  index: number;
  hideLabel?: boolean;
  MenuProps?: Partial<MenuProps>;
}

export const STATE_TAX_SITUS_HELP_BODY =
  "Indicates the state where this asset is physically located for estate tax purposes. Assets held in the owner's state of domicile are taxed under that state's domicile rules as a state resident. Real or tangible personal property located in another state may be subject to that state's non-resident estate tax.";

export const AssetStateField = ({
  index,
  hideLabel,
  MenuProps: menuProps,
}: AssetStateFieldProps) => {
  const { control } = useFormContext<Fields>();
  const { primaryClients } = useUnguardedHouseholdDetailsContext();

  const fieldName =
    `${STRUCTURED_ASSETS_SUBFORM_NAMESPACE}.assets.${index}.taxSitus` as const satisfies SubformField;

  // Empty option only visible for legacy assets without a persisted situs.
  const currentValue = useWatch<Fields>({ control, name: fieldName });
  const showEmptyValue = !currentValue;

  const groupedOptions = useMemo(() => {
    const sortedClients = orderBy(primaryClients ?? [], [
      (c) => c.createdAt,
      (c) => c.id,
    ]);
    const grantorOptions: SelectInputOption[] = sortedClients.map((client) => {
      const statePrefix = client.stateCode ? `${client.stateCode} - ` : '';
      return {
        value: `${TAX_SITUS_GRANTOR_PREFIX}${client.id}`,
        display: `${statePrefix}(${client.displayGivenName}'s resident state)`,
      };
    });

    const stateCodeOptions: SelectInputOption[] = (
      Object.keys(STATE_CODE_TO_NAME) as StateCode[]
    ).map((stateCode) => ({
      value: `${TAX_SITUS_STATE_PREFIX}${stateCode}`,
      display: stateCode,
    }));

    return getOptionsWithGroupLabels([
      { label: 'State of domicile', options: grantorOptions },
      { label: null, options: stateCodeOptions },
    ]);
  }, [primaryClients]);

  return (
    <FormAwareSelectInput<Fields>
      fieldName={fieldName}
      label="State"
      hideLabel={hideLabel}
      control={control}
      options={groupedOptions}
      showEmptyValue={showEmptyValue}
      contextualHelp={<PopperContent body={STATE_TAX_SITUS_HELP_BODY} />}
      MenuProps={menuProps}
    />
  );
};
